import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { subscriptionService, SubscriptionSummary, TenantInvoice } from '../services/api';

/**
 * Warns about an unpaid invoice BEFORE it costs the tenant anything.
 *
 * ReadOnlyBanner is the other half of this and is deliberately reactive: it only
 * appears once a write has already been refused with a 402, i.e. after the tenant
 * is locked. By then the damage is done — the first anyone hears about it is a
 * button that stopped working mid-task.
 *
 * This one is proactive. The billing cycle runs the same ladder every day:
 *
 *     due date passes            -> past_due   (writes blocked for everyone,
 *                                               tenant_admin keeps read access)
 *     + DEFAULT_GRACE_DAYS (7)   -> suspended  (nobody can log in)
 *
 * so both dates are known from the invoice's own due_date, and we can say exactly
 * what happens and when rather than "please pay".
 *
 * Only 'sent' invoices count, matching the backend: OPEN_STATUSES = ("sent",).
 * A draft never triggers dunning, so warning about one would be a lie.
 */

const WARN_WITHIN_DAYS = 3;   // start nudging this long before writes are blocked
const GRACE_DAYS = 7;         // mirrors billing.DEFAULT_GRACE_DAYS

const money = (v: number | null, currency = 'INR') =>
  v === null ? '' : `${currency === 'INR' ? '₹' : ''}${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const fmtDate = (d: Date) =>
  d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

/** Whole days from now until `d`, rounded toward the deadline (today = 0). */
const daysUntil = (d: Date) => {
  const a = new Date(); a.setHours(0, 0, 0, 0);
  const b = new Date(d); b.setHours(0, 0, 0, 0);
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
};

type Notice = { tone: 'warn' | 'urgent'; text: string };

function noticeFor(inv: TenantInvoice): Notice | null {
  if (!inv.due_date) return null;
  const due = new Date(inv.due_date);
  if (isNaN(due.getTime())) return null;

  const toDue = daysUntil(due);
  const suspendsOn = new Date(due); suspendsOn.setDate(suspendsOn.getDate() + GRACE_DAYS);
  const toSuspend = daysUntil(suspendsOn);
  const amount = money(inv.total, inv.currency);

  // Already past the due date: writes are blocked (or about to be on the next
  // daily cycle) and the clock is now running toward full suspension.
  if (toDue < 0) {
    if (toSuspend <= 0) {
      return { tone: 'urgent', text: `Invoice ${inv.invoice_number} (${amount}) is overdue and this workspace is suspended. Pay to restore access.` };
    }
    return {
      tone: 'urgent',
      text: `Invoice ${inv.invoice_number} (${amount}) is overdue. Editing is blocked, and everyone loses access on ${fmtDate(suspendsOn)} unless it is paid.`,
    };
  }

  // Due today, or within the warning window.
  if (toDue === 0) {
    return { tone: 'urgent', text: `Invoice ${inv.invoice_number} (${amount}) is due today. Editing will be blocked tomorrow unless it is paid.` };
  }
  if (toDue <= WARN_WITHIN_DAYS) {
    return {
      tone: 'warn',
      text: `Invoice ${inv.invoice_number} (${amount}) is due in ${toDue} day${toDue === 1 ? '' : 's'}, on ${fmtDate(due)}. Editing is blocked after that.`,
    };
  }
  return null;
}

// How long before a TRIAL ends we start warning. An active subscription
// renews itself and is not warned about — see noticeForSubscription.
const PERIOD_WARN_DAYS = 7;

/**
 * The subscription itself running out, independent of any invoice. An invoice
 * is only raised at the end of a period, so without this a tenant got no word
 * at all until it was already too late -- and a lapsed tenant's users were
 * simply signed out.
 */
function noticeForSubscription(sub: SubscriptionSummary): Notice | null {
  const state = sub.state;
  if (state === 'past_due' || state === 'suspended') {
    return { tone: 'urgent', text: "Your organisation's subscription has expired. Ask your admin to renew it to restore full access." };
  }
  // A trial genuinely ends: nothing renews it, and somebody has to act before
  // the date. An ACTIVE subscription does not — the billing run raises the next
  // invoice and rolls the period on its own.
  //
  // Warning about one was telling a paying customer their subscription "expires
  // in 2 days, ask your admin to renew" every month in the run-up to their own
  // renewal, for a thing that needed nobody. It also survived a plan change,
  // because approving one never moved the period end, so it read as a banner
  // that would not go away. If that renewal does fail, the unpaid invoice is
  // what says so above — with the amount and the date access stops.
  const end = state === 'trial' ? sub.trial_ends_at : null;
  if (!end) return null;
  const d = new Date(end);
  if (isNaN(d.getTime())) return null;
  const left = daysUntil(d);
  if (left > PERIOD_WARN_DAYS) return null;
  const what = state === 'trial' ? 'Your free trial' : 'Your subscription';
  if (left < 0) {
    return { tone: 'urgent', text: `${what} ended on ${fmtDate(d)}. Ask your admin to renew it so access is not interrupted.` };
  }
  if (left === 0) {
    return { tone: 'urgent', text: `${what} expires today. Ask your admin to renew it so access is not interrupted.` };
  }
  return {
    tone: left <= 2 ? 'urgent' : 'warn',
    text: `${what} expires in ${left} day${left === 1 ? '' : 's'}, on ${fmtDate(d)}. Ask your admin to renew it.`,
  };
}

export default function PaymentDueBanner() {
  const [notice, setNotice] = useState<Notice | null>(null);

  // /subscription is admin-only (AdminLayoutRoute), so offering everyone a link
  // to it would drop ordinary users on an "Access Restricted" page. They still
  // see the warning — it is their access that is about to stop — but the call to
  // action goes only to the people who can act on it.
  const role = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}').role || ''; }
    catch { return ''; }
  })();
  const canPay = role === 'tenant_admin' || role === 'super_admin';

  const check = useCallback(() => {
    // The invoice notice is the more specific one (it names the amount and the
    // date editing stops), so it wins; otherwise warn about the period itself.
    // Each call fails on its own: no tenant context (super_admin), offline, or a
    // 402 means nothing useful to say from that source.
    Promise.all([
      subscriptionService.getInvoices().then((r) => r.data || []).catch(() => [] as TenantInvoice[]),
      subscriptionService.getSubscription().then((r) => r.data).catch(() => null),
    ]).then(([invoices, sub]) => {
      const open = invoices.filter((i) => i.status === 'sent' && i.due_date);
      open.sort((a, b) => (a.due_date! < b.due_date! ? -1 : 1));   // the one that bites first
      const fromInvoice = open.length ? noticeFor(open[0]) : null;
      setNotice(fromInvoice || (sub ? noticeForSubscription(sub) : null));
    });
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('token')) return;
    check();
    // Re-check when the tab regains focus: the state changes on a daily server
    // cycle, and someone may have paid in the meantime.
    const onFocus = () => check();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [check]);

  if (!notice) return null;

  const urgent = notice.tone === 'urgent';
  return (
    <div
      role="status"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
        flexWrap: 'wrap', padding: '9px 16px', fontSize: '13px', fontWeight: 500,
        fontFamily: "'Poppins', sans-serif",
        background: urgent ? '#FEE2E2' : '#FEF3C7',
        color: urgent ? '#991B1B' : '#92400E',
        borderBottom: `1px solid ${urgent ? '#FCA5A5' : '#FCD34D'}`,
      }}
    >
      <span>{notice.text}</span>
      {canPay ? (
        <Link
          to="/subscription"
          style={{
            padding: '3px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: 600,
            textDecoration: 'none', whiteSpace: 'nowrap',
            background: urgent ? '#991B1B' : '#92400E', color: '#FFFFFF',
          }}
        >
          Renew
        </Link>
      ) : (
        <span style={{ fontSize: '12px', opacity: 0.85 }}>Contact your administrator.</span>
      )}
    </div>
  );
}
